import React, { useCallback, useState, useMemo } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { WbsTaskSearchResult } from 'graphql/types';
import { SearchableChip } from 'modules/common/components/Chip/SearchableChip';
import { LabelType } from 'graphql/types';
import { SimpleChip } from 'modules/common';
import {
  LabelState,
  useAcceptCptSuggestion
} from 'modules/timesheet-labels/hooks';
import { useMessage } from 'modules/intl';
import { ChipProps } from '../schema';
import DependantLabels from '../DependantLabels';
import { TimesheetLabel } from '../../labelSchema';
import { useChipState } from '../../../common/hooks/useChipState';
import { useFeatureFlag } from '../../../common/hooks';
import { useLabelState } from '../../hooks/useLabelState';
import useTasks from './useTasks';
import RenderOption from './RenderOption';
import useTaskOnSelect from './useTaskOnSelect';

const useStyles = makeStyles(() => ({
  simpleChip: {
    maxWidth: '100%',
    minWidth: '50px',
    cursor: 'not-allowed'
  }
}));

const useAutoCompleteStyles = makeStyles((theme) => ({
  listbox: {
    maxHeight: theme.spacing(19)
  }
}));

const getOptionSelected = (
  option: WbsTaskSearchResult,
  selected: WbsTaskSearchResult
) => option.id === selected?.id;

export const TaskChip: React.FC<ChipProps> = ({
  dependantLabels,
  onSelect,
  disabled = false
}: ChipProps) => {
  const isEmptyLabelsEnabled = useFeatureFlag('isEmptyLabelsEnabled', false);
  const classes = useStyles();
  const autoCompleteStyles = useAutoCompleteStyles();

  const [taskChipOpen, setTaskChipOpen] = useState(false);

  const {
    label,
    options,
    invalidTaskIds,
    handleSearch,
    loading,
    hasMore
  } = useTasks(dependantLabels, taskChipOpen);

  // TIDP-1774: Remove useChipState when isEmptyLabelsEnabled is fully rolled out.
  const { isEmptyValue: isEmpty } = useChipState(label);
  const labelState = useLabelState(label);

  const openHandler = useCallback(() => {
    handleSearch('');
    setTaskChipOpen(true);
  }, [handleSearch]);

  const onClose = useCallback(() => {
    setTaskChipOpen(false);
  }, [setTaskChipOpen]);

  const onTaskSelect = useTaskOnSelect(onSelect, dependantLabels, onClose);

  const onChipDelete = useCallback(() => {
    onTaskSelect(null, null);
  }, [onTaskSelect]);

  const getOptionLabel = useCallback(
    (option: WbsTaskSearchResult) => option.displayText || '',
    []
  );

  const renderOption = useCallback(
    (option: WbsTaskSearchResult, searchText: string) => {
      return <RenderOption option={option} searchText={searchText} />;
    },
    []
  );

  const getOptionDisabled = useCallback(
    (option: WbsTaskSearchResult) =>
      (option.id && invalidTaskIds.includes(option.id)) || false,
    [invalidTaskIds]
  );

  const notSelected = useMessage('timesheetLabels.notSelected');
  const labels = useMemo(
    () =>
      Object.values(dependantLabels)
        .filter(
          (label) =>
            ![LabelType.Task, LabelType.Billing].includes(label?.label?.type)
        )
        .map((labelData) => {
          if (labelData.label?.value) {
            return labelData?.label as TimesheetLabel;
          }
          return {
            ...labelData?.label,
            ...{ value: { stringValue: notSelected } }
          };
        }),
    [dependantLabels, notSelected]
  );

  const handleDelete = useMemo(() => {
    if (isEmptyLabelsEnabled && labelState !== LabelState.NOT_SELECTED) {
      return { handleDelete: onChipDelete };
    }

    if (!isEmptyLabelsEnabled && !isEmpty) {
      return { handleDelete: onChipDelete };
    }

    return null;
  }, [labelState, isEmpty, isEmptyLabelsEnabled, onChipDelete]);

  const acceptTaskSuggestion = useAcceptCptSuggestion(dependantLabels, 'task');

  const handleAcceptSuggestion = useCallback(() => {
    acceptTaskSuggestion();
  }, [acceptTaskSuggestion]);

  const value = useMemo(
    () =>
      label?.value
        ? {
            id: label.value.idValue,
            displayText: label.value.stringValue ?? '',
            parents: []
          }
        : undefined,
    [label]
  );

  const chip = useMemo(
    () =>
      disabled ? (
        <SimpleChip
          className={classes.simpleChip}
          label={label}
          key={label?.id}
          disabled={disabled}
          acceptSuggestion={handleAcceptSuggestion}
          {...handleDelete}
        />
      ) : (
        <SearchableChip
          label={label}
          onOpen={openHandler}
          onClose={onClose}
          handleSearch={handleSearch}
          loading={loading}
          options={options}
          onSelect={onTaskSelect}
          disabled={disabled}
          getOptionLabel={getOptionLabel}
          renderOption={renderOption}
          getOptionDisabled={getOptionDisabled}
          {...handleDelete}
          getOptionSelected={getOptionSelected}
          value={value}
          hasMore={hasMore}
          acceptSuggestion={handleAcceptSuggestion}
          overrideClasses={autoCompleteStyles}
        >
          {labels && <DependantLabels dependantLabels={labels} disabled />}
        </SearchableChip>
      ),
    [
      disabled,
      classes.simpleChip,
      label,
      openHandler,
      onClose,
      handleSearch,
      loading,
      options,
      onTaskSelect,
      getOptionLabel,
      renderOption,
      getOptionDisabled,
      handleDelete,
      value,
      hasMore,
      handleAcceptSuggestion,
      autoCompleteStyles,
      labels
    ]
  );

  return <>{chip}</>;
};

export default TaskChip;
